<template>
  <component-wrapper class="product-family-fi">
    <div class="product-family-fi__scroll-target" />
    <headline-fi
      class="fi-hidden sm:fi-block"
      tag="h2"
      :size="24"
    >
      {{ $dict.get('product-Family.Anchor-Link-Text') }}
    </headline-fi>
    <product-list-view-fi
      ref="productListView"
      :key="key"
      :shop-type="shopType"
      :dev-data="devData"
      :family-line-drawing="familyLineDrawing"
      :approvals="approvals"
      :catalog-data="catalogData"
      click-source="product-family"
      class="product-family-fi__container"
      @ready="onReady"
    />
  </component-wrapper>
</template>

<script setup lang="ts">
import { HeadlineFi } from 'atoms';
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import ProductListViewFi from '@/components/shop-modules/product-list-view/ProductListViewFi.vue';
import { useAccessPrivileges } from '@/composables/accessPrivileges';
import { useFischer } from '@/composables/fischerPlugin';
import { useProductListStore } from '@/store/pinia/product-list';

defineOptions({ name: 'ProductFamilyFi' });

const props = withDefaults(
  defineProps<{
    devData?: Record<string, any> | null;
    familyLineDrawing?: string | null;
    approvals?: any[];
    catalogData?: Record<string, any> | null;
  }>(),
  {
    devData: null,
    familyLineDrawing: null,
    approvals: () => [],
    catalogData: null,
  },
);

const emit = defineEmits<{
  (e: 'scrollToVariants'): void;
}>();

const fischer = useFischer();
const { hasAvailabilityStatusOnlyPermission } = useAccessPrivileges();
const productListStore = useProductListStore();

const scrolledToProductList = ref(false);
const productListView = useTemplateRef<InstanceType<typeof ProductListViewFi>>('productListView');

const shopType = computed({
  get() {
    return productListStore.shopType;
  },
  set(value) {
    productListStore.setShopType(value);
  },
});

const key = computed(() => {
  if (IS_SHOWROOM && props.devData) {
    return `${shopType.value}-${props.devData.id}-${props.devData.baseSiteId}-${props.devData.language}`;
  }

  return 'product-list-key';
});

function setShopType() {
  let type;

  if (fischer.shop.isRetailConnect()) {
    type = 'retailConnect';
  }
  if (fischer.shop.isRetailConnectCart()) {
    type = 'retailConnectCart';
  }
  if (fischer.shop.isClosedShop()) {
    type = 'closedShop';
  }

  if (hasAvailabilityStatusOnlyPermission.value) {
    type = 'retailConnect';
  }

  shopType.value = type || 'default';
}

async function scrollToProductList() {
  if (IS_SSR) {
    return;
  }
  if (/\bproductList\b/.test(window.location.hash)) {
    await nextTick();
    emit('scrollToVariants');
  }
}

function onReady() {
  if (scrolledToProductList.value) {
    return;
  }
  scrolledToProductList.value = true;
  scrollToProductList();
}

function changeFacet(changes: any) {
  productListView.value?.changeFacet(changes);
}

function clearFacets() {
  productListView.value?.clearAllFacets();
}

defineExpose({ changeFacet, clearFacets });

// created
setShopType();

onMounted(() => {
  // we do this as soon as possible and again after results are loaded, which hopefully will happen in transit
  scrollToProductList();
});
</script>

<style scoped src="./product-family-fi" />
